import type { MouseEvent, ReactNode } from 'react'
import { FileText, FolderOpen } from 'lucide-react'
import {
  type DocumentPeekAddToChatDetail,
  DocumentPeekMenuItems,
  PeekLocationLine,
  peekParentFolderLabel,
} from '@/components/features/document-peek-chrome'
import { PeekHeader } from './peek-header'

export type DocumentPeekHeaderProps = {
  kind: 'document' | 'folder'
  title: string
  path?: string
  /** Kept so existing callers compile. The header does not show a type chip. */
  docType?: string
  /** Kind icon override (image, skill). Defaults to FileText / FolderOpen. */
  icon?: ReactNode
  /** Kept so existing callers compile. The header does not show a type chip. */
  typeLabel?: string
  /** Adds "Add to chat" to the overflow (shared `add-to-chat` event). */
  addToChat?: DocumentPeekAddToChatDetail
  /** Forwards the click so Cmd/Ctrl resolves new tab vs current tab (ADR-0016). */
  onOpen: (event: MouseEvent<HTMLButtonElement>) => void
  onClose: () => void
  testId?: string
  actionsTestId?: string
  openTestId?: string
}

/**
 * Document and folder peek recipe: kind icon, containing folder / title,
 * ..., open, close. The type word ("Document") is not the header.
 */
export function DocumentPeekHeader({
  kind,
  title,
  path,
  icon,
  addToChat,
  onOpen,
  onClose,
  testId,
  actionsTestId,
  openTestId,
}: DocumentPeekHeaderProps) {
  const isFolder = kind === 'folder'
  const parent = peekParentFolderLabel(path)
  return (
    <PeekHeader
      row
      testId={testId}
      icon={
        icon ??
        (isFolder ? (
          <FolderOpen className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
        ) : (
          <FileText className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
        ))
      }
      chips={
        <PeekLocationLine parent={parent} title={title} />
      }
      dropdownContent={
        isFolder ? undefined : (
          <DocumentPeekMenuItems title={title} copyPath={path} addToChat={addToChat} />
        )
      }
      dropdownTestId={actionsTestId}
      onOpen={onOpen}
      openTitle={isFolder ? 'Open folder' : 'Open in tab'}
      openTestId={openTestId}
      onClose={onClose}
    />
  )
}
